import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react'
import {getConfig} from './config'
import {getUserToken} from './token'
import {getLogger} from './logging'

const {guestPortalApiUrl} = getConfig()
const logger = getLogger('guestPortalApi')

export const guestPortalApi = createApi({
  reducerPath: 'guestPortalApi',
  baseQuery: fetchBaseQuery({
    baseUrl: guestPortalApiUrl,
    prepareHeaders: async (headers): Promise<Headers> => {
      try {
        // injects authorization token into request headers
        const token = getUserToken()
        if (token?.length) {
          headers.set('Authorization', `Bearer ${token}`)
        }
      } catch (e) {
        logger.error({
          message: 'Error occurred setting guest token on HTTP request',
          errorData: {
            error: e
          }
        })
      }
      return headers
    }
  }),
  tagTypes: [],
  endpoints: builder => ({
    cancelReservation: builder.mutation({
      query({reservationId, dealId}: {reservationId: string; dealId: string}) {
        return {
          url: `/reservations/${reservationId}/cancel?dealId=${dealId}`,
          method: 'PUT'
        }
      }
      // invalidatesTags: ['Reservations']
    }),
    quoteModifyReservation: builder.mutation({
      query({
        reservationId,
        arrival,
        departure
      }: {
        reservationId: string
        arrival: string
        departure: string
      }) {
        return {
          url: `/reservations/${reservationId}/modify/quote`,
          method: 'POST',
          body: {arrival, departure}
        }
      }
    }),
    applyModifyReservation: builder.mutation({
      query({
        reservationId,
        arrival,
        departure,
        expectedNewTotal
      }: {
        reservationId: string
        arrival: string
        departure: string
        expectedNewTotal: {amount: number; currency: string}
      }) {
        return {
          url: `/reservations/${reservationId}/modify`,
          method: 'PUT',
          body: {arrival, departure, expectedNewTotal}
        }
      }
    }),
    getOTAReservations: builder.query({
      query({reservationStatus}) {
        return `/reservations/ota?reservationStatus=${reservationStatus}`
      }
    }),
    postOTAReservation: builder.mutation({
      query({confirmationCode, lastName, ota}) {
        return {
          url: `/reservations/link-ota-reservation`,
          method: 'POST',
          body: {
            confirmationCode,
            lastName,
            ota
          }
        }
      }
    }),
    getMemberReservations: builder.query({
      query({reservationStatus, reservationId, sortOrder}) {
        const params = new URLSearchParams()
        if (reservationStatus) {
          params.append('reservationStatus', reservationStatus)
        }
        if (reservationId) {
          params.append('reservationId', reservationId)
        }
        if (sortOrder) {
          params.append('sortOrder', sortOrder)
        }
        return `/reservations?${params.toString()}`
      },
      providesTags: ['Reservations']
    }),
    getMemberReservation: builder.query({
      query({reservationId}) {
        return `/reservations/${reservationId}`
      },
      providesTags: ['Reservations']
    }),
    getMemberReservationsDetailed: builder.query({
      query({reservationStatus, reservationId}) {
        const params = new URLSearchParams()
        if (reservationStatus) {
          params.append('reservationStatus', reservationStatus)
        }
        if (reservationId) {
          params.append('reservationId', reservationId)
        }
        return `/reservations-detailed?${params.toString()}`
      },
      providesTags: ['Reservations']
    }),
    getContactInfo: builder.query({
      query({}) {
        return `/contact`
      },
      providesTags: ['ContactInfo']
    }),
    postContactNotificationPreferences: builder.mutation({
      query({smsOptOut, marketingEmailUnsubscribed}) {
        return {
          url: `/contact/notifications`,
          method: 'POST',
          body: {
            smsOptOut,
            marketingEmailUnsubscribed
          }
        }
      },
      invalidatesTags: ['ContactInfo']
    }),
    getProperty: builder.query({
      query({propertyId}) {
        return `/properties/${propertyId}`
      },
      providesTags: ['Property']
    }),
    getCMSProperties: builder.query({
      query({}) {
        return `/cms/properties`
      },
      transformResponse: (response: any) => {
        return response.data.reduce((acc: any, property: any) => {
          acc[property.propertyApaleoId] = property
          return acc
        }, {})
      },
      providesTags: []
    }),
    getPMSProperties: builder.query({
      query({}) {
        return `/pms/properties`
      },
      transformResponse: (response: any) => {
        return response.data.reduce((acc: any, property: any) => {
          acc[property.id] = property
          return acc
        }, {})
      },
      providesTags: []
    }),
    getPaymentMethods: builder.query({
      query({reservation}) {
        return `/payment-methods?propertyId=${reservation.propertyId}`
      },
      providesTags: []
    }),
    getPaymentSession: builder.query({
      query({reservation}) {
        return {
          url: `/payment-session`,
          method: 'POST',
          body: {
            reservationId: reservation.reservationId,
            bookingId: reservation.bookingId,
            propertyId: reservation.propertyId,
            returnUrl: 'http://www.example.com',
            countryCode: 'US'
          }
        }
      },
      providesTags: []
    }),
    getReservationInvoice: builder.query({
      query({reservationId}) {
        return `/reservations/${reservationId}/invoice`
      }
    })
  })
})

export const {
  useCancelReservationMutation,
  useQuoteModifyReservationMutation,
  useApplyModifyReservationMutation,
  usePostOTAReservationMutation,
  useGetOTAReservationsQuery,
  useGetMemberReservationsQuery,
  useGetMemberReservationQuery,
  useGetMemberReservationsDetailedQuery,
  useGetPropertyQuery,
  useGetCMSPropertiesQuery,
  useGetPMSPropertiesQuery,
  useGetPaymentMethodsQuery,
  useGetPaymentSessionQuery,
  useGetContactInfoQuery,
  usePostContactNotificationPreferencesMutation,
  useGetReservationInvoiceQuery
} = guestPortalApi
